import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  Paper,
} from "@mui/material";
import {
  Assignment,
  AccountCircle,
  Description,
  Business,
  Category,
  Person,
  AttachMoney,
  DateRange,
  LocationOn,
  Email,
  Phone,
  Fax,
  Flag,
  Info,
} from "@mui/icons-material";

const ViewQuote = ({ quote }) => {
  const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {icon && <Box sx={{ mr: 1, color: '#1976d2' }}>{icon}</Box>}
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '140px', color: '#555' }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ ml: 1, color: '#333' }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value}%`;
  };

  const StatusChip = ({ label, value, color = "default" }) => {
    if (value === null || value === undefined) return null;
    return (
      <Chip 
        label={`${label}: ${value ? 'Yes' : 'No'}`}
        color={value ? color : "default"}
        variant={value ? "filled" : "outlined"}
        size="small"
        sx={{ mr: 1, mb: 1 }}
      />
    );
  };

  if (!quote) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        {/* Quote Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Quote Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Quote Number" value={quote.quoteNumber} />
              <InfoRow label="PO Number" value={quote.poNumber} />
              <InfoRow label="Client Job Number" value={quote.clientJobNumber} />
              <InfoRow label="Global Job Number" value={quote.globalJobNumber} />
              <InfoRow label="Project Type" value={quote.projectType} />
              <InfoRow label="Template" value={quote.template} />
              <InfoRow label="Record ID" value={quote.recordid} />
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label={quote.status || 'N/A'}
                  color={quote.status === 'Active' ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Client Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Account Number" value={quote.accountNumber} />
              <InfoRow label="Client Code" value={quote.clientCode} />
              <InfoRow label="Sales Person" value={quote.salesPerson} />
              <InfoRow label="Project Manager" value={quote.projectManager} />
              <InfoRow label="Created By" value={quote.createdBy} />
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Contact Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing Contact
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Name" value={quote.billingName} />
              <InfoRow label="Contact" value={quote.billingContact} />
              <InfoRow 
                label="Email" 
                value={quote.billingEmail} 
                icon={<Email fontSize="small" />}
              />
              <InfoRow 
                label="Phone" 
                value={quote.billingPhone} 
                icon={<Phone fontSize="small" />}
              />
              <InfoRow 
                label="Fax" 
                value={quote.billingFax} 
                icon={<Fax fontSize="small" />}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Address Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing Address
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Address" value={quote.billingAddress} />
              <InfoRow label="Address 2" value={quote.billingAddress2} />
              <InfoRow label="City" value={quote.billingCity} />
              <InfoRow label="State" value={quote.billingState} />
              <InfoRow label="Zip Code" value={quote.billingZip} />
            </CardContent>
          </Card>
        </Grid>

        {/* Dates Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DateRange sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Important Dates
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Date Created" value={formatDate(quote.dateCreated)} />
              <InfoRow label="Date Set" value={formatDate(quote.dateSet)} />
              <InfoRow label="Expiration Date" value={formatDate(quote.expirationDate)} />
            </CardContent>
          </Card>
        </Grid>

        {/* Pricing Summary Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Pricing Summary
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Quoted Amount" value={formatCurrency(quote.quotedAmount)} />
              <InfoRow label="List Price" value={formatCurrency(quote.listPrice)} />
              <InfoRow label="Adjustable Total" value={formatCurrency(quote.adjustableTotal)} />
              <InfoRow label="Non-adjustable Total" value={formatCurrency(quote.nonadjustableTotal)} />
              <InfoRow label="Miscellaneous Charges" value={formatCurrency(quote.miscellaneousCharges)} />
            </CardContent>
          </Card>
        </Grid>

        {/* Discounts & Charges Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Discounts & Charges
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Discount Percent" value={formatPercent(quote.discountPercent)} />
              <InfoRow label="Discount Amount" value={formatCurrency(quote.discountAmount)} />
              <InfoRow label="Surcharge Percent" value={formatPercent(quote.surchargePercent)} />
              <InfoRow label="Surcharge Amount" value={formatCurrency(quote.surchargeAmount)} />
              <InfoRow label="Sales Tax Percent" value={formatPercent(quote.salesTaxPercent)} />
            </CardContent>
          </Card>
        </Grid>

        {/* Options & Flags Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Flag sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Options & Flags
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <StatusChip 
                  label="Apply Line Item Discount" 
                  value={quote.applyLineItemDiscount} 
                  color="primary" 
                />
                <StatusChip 
                  label="Apply Discount to Misc" 
                  value={quote.applyDiscToMisc} 
                  color="secondary" 
                />
                <StatusChip 
                  label="Quote By Job" 
                  value={quote.quoteByJob} 
                  color="info" 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Description & Comments Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Description sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Additional Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {quote.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
                    Description:
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                    <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                      {quote.description}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {quote.comments && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
                    Comments:
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                    <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                      {quote.comments}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewQuote;